import * as React from 'react';
import { PageErrorState } from '../../../components/shared/PageErrorState';
import { formatDateWithWeekday, formatTime } from '../../../lib/datetime';
import { useCalendarConnection } from '../hooks/useCalendarConnection';
import { useMeetings } from '../hooks/useMeetings';
import type { CalendarEvent } from '../services/meetingsApi';
import './meetings-panel.css';

interface MeetingsPanelProps {
  /** Increment to trigger a re-fetch (e.g. after sync) */
  refreshKey?: number;
  /** Sync error from parent - shows a banner if set */
  syncError: string | null;
  /** Called to dismiss/reset the sync error (e.g. after connecting) */
  onResetSyncError: () => void;
}

interface EventSection {
  label: string;
  /** Events in this section */
  events: CalendarEvent[];
  /** True for the "Today" section - hides the per-row date */
  isToday: boolean;
}

/** Groups events into Today / This Week / Month buckets using local time. */
function groupEvents(events: CalendarEvent[]): EventSection[] {
  const now = new Date();
  const todayY = now.getFullYear();
  const todayM = now.getMonth();
  const todayD = now.getDate();

  // Monday of the current week (local time)
  const dayOfWeek = now.getDay(); // 0 = Sun
  const daysFromMon = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const weekMonday = new Date(now);
  weekMonday.setDate(now.getDate() - daysFromMon);
  weekMonday.setHours(0, 0, 0, 0);
  const weekSunday = new Date(weekMonday);
  weekSunday.setDate(weekMonday.getDate() + 6);
  weekSunday.setHours(23, 59, 59, 999);

  const todayEvents: CalendarEvent[] = [];
  const weekEvents: CalendarEvent[] = [];
  // Preserve insertion order for month buckets
  const monthOrder: string[] = [];
  const monthBuckets = new Map<string, CalendarEvent[]>();

  for (const event of events) {
    const d = new Date(event.startTime);
    if (d.getFullYear() === todayY && d.getMonth() === todayM && d.getDate() === todayD) {
      todayEvents.push(event);
    } else if (d >= weekMonday && d <= weekSunday) {
      weekEvents.push(event);
    } else {
      const key = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
      if (!monthBuckets.has(key)) {
        monthOrder.push(key);
        monthBuckets.set(key, []);
      }
      monthBuckets.get(key)!.push(event);
    }
  }

  const sections: EventSection[] = [];

  if (todayEvents.length > 0) {
    const todayLabel = now.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    sections.push({ label: `Today · ${todayLabel}`, events: todayEvents, isToday: true });
  }

  if (weekEvents.length > 0) {
    sections.push({ label: 'This Week', events: weekEvents, isToday: false });
  }

  for (const key of monthOrder) {
    sections.push({ label: key, events: monthBuckets.get(key)!, isToday: false });
  }

  return sections;
}

/**
 * Meetings panel - main content for the Meetings page.
 *
 * Shows upcoming calendar events grouped by Today / This Week / Month.
 * The page-level header and Sync button are handled by MeetingsPage.
 */
export function MeetingsPanel({ refreshKey, syncError, onResetSyncError }: MeetingsPanelProps) {
  const { events, loading, error, refetch } = useMeetings(refreshKey);
  const {
    status: connectionStatus,
    connecting,
    error: connectionError,
    connect,
  } = useCalendarConnection();

  const isConnected = connectionStatus?.connected === true;
  const isNotConnected = connectionStatus != null && !isConnected;
  const showConnectCta = syncError === 'CONNECTION_NOT_FOUND' || isNotConnected;

  const handleConnect = React.useCallback(async () => {
    await connect();
    onResetSyncError();
  }, [connect, onResetSyncError]);

  const sections = React.useMemo(() => groupEvents(events), [events]);

  return (
    <div className="meetings-panel">
      {showConnectCta && (
        <div className="ss-panel__banner ss-panel__banner--warning" role="alert">
          {connectionError ? (
            <>Connect failed: {connectionError}</>
          ) : (
            <>
              Connect your personal Google Calendar to sync events.{' '}
              <button
                type="button"
                className="ss-panel__banner-action"
                onClick={() => void handleConnect()}
                disabled={connecting}
              >
                {connecting ? 'Connecting…' : 'Connect Google Calendar'}
              </button>
            </>
          )}
        </div>
      )}

      {syncError && syncError !== 'CONNECTION_NOT_FOUND' && (
        <div className="ss-panel__banner ss-panel__banner--error" role="alert">
          Sync failed: {syncError}
        </div>
      )}

      {loading && <p className="meetings-panel__state">Loading meetings…</p>}

      {!loading && error && (
        <div className="meetings-panel__state meetings-panel__state--error">
          <PageErrorState message={error} onRetry={refetch} />
        </div>
      )}

      {!loading && !error && sections.length === 0 && (
        <div className="meetings-panel__empty">
          <p className="meetings-panel__empty-title">No upcoming meetings</p>
          <p className="meetings-panel__empty-body">
            Connect your Google Calendar in Settings, then sync to see events here.
          </p>
        </div>
      )}

      {!loading && !error && sections.length > 0 && (
        <div className="meetings-panel__sections">
          {sections.map((section) => (
            <div key={section.label} className="meetings-panel__section">
              <div className="meetings-panel__section-header">{section.label}</div>
              <ul className="meetings-panel__list" aria-label={section.label}>
                {section.events.map((event) => (
                  <MeetingRow key={event.id} event={event} showDate={!section.isToday} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MeetingRow({ event, showDate }: { event: CalendarEvent; showDate: boolean }) {
  const isCancelled = event.status === 'cancelled';
  const isTentative = event.status === 'tentative';

  return (
    <li
      className={`meetings-panel__event${isCancelled ? ' meetings-panel__event--cancelled' : ''}`}
    >
      <div className="meetings-panel__event-time">
        {showDate && (
          <span className="meetings-panel__event-date">
            {formatDateWithWeekday(event.startTime)}
          </span>
        )}
        <span className="meetings-panel__event-clock">
          {formatTime(event.startTime)}–{formatTime(event.endTime)}
        </span>
      </div>
      <div className="meetings-panel__event-body">
        <p className="meetings-panel__event-title">
          {event.title ?? '(No title)'}
          {isTentative && (
            <span className="meetings-panel__badge meetings-panel__badge--tentative">
              Tentative
            </span>
          )}
          {isCancelled && (
            <span className="meetings-panel__badge meetings-panel__badge--cancelled">
              Cancelled
            </span>
          )}
        </p>
        {(event.organizerName ?? event.organizerEmail) && (
          <p className="meetings-panel__event-meta">
            {event.organizerName ?? event.organizerEmail}
          </p>
        )}
        {event.visibilityClassification && (
          <span
            className={`meetings-panel__badge meetings-panel__badge--${event.visibilityClassification}`}
          >
            {event.visibilityClassification}
          </span>
        )}
      </div>
    </li>
  );
}
